// HU-32a (MG-ILE-63) · tareas 32a-5, 32a-6 y 32a-7. El ciclo de la sesión, en un solo sitio.
//
// La raíz de la aplicación (`App.tsx`) pinta según la FASE que devuelve este hook:
//
//   preparando  → con Entra, MSAL procesa la vuelta de Microsoft (`handleRedirectPromise`).
//   entrada     → sin cuenta: «Ingresar con mi cuenta» (Entra) o la entrada simulada.
//   cargando    → `GET /sesion` (o `POST /sesion/inicio` en la vuelta de `loginRedirect`).
//   error       → Microsoft o `GET /sesion` no respondieron, o el token no es del tenant (401,
//                 HU-32a-C2). La pantalla dice «No se pudo iniciar sesión…» y nunca el error crudo.
//   sin_acceso  → `tiene_acceso = false` (sin roles o desactivado, HU-32a-C4, HU-32b-C4).
//   lista       → el `AppShell` con el menú y la cabecera del servidor.
//
// Además escucha al cliente de API: un 401 en cualquier petición marca la sesión como
// caducada (aviso «Tu sesión terminó. Vuelve a ingresar.», HU-32a-C5) y un 403 vuelve a
// preguntar `GET /sesion`: si la persona perdió el acceso entre medias (la desactivaron), su
// siguiente petición la lleva a «Sin acceso» en vez de a un «No tienes acceso a esta opción»
// suelto en la pantalla en la que estaba.

import { useCallback, useEffect, useRef, useState } from 'react';
import { consultarSesion, iniciarSesion } from '../api/sesion';
import type { SesionDelServidor } from '../api/sesion';
import { escucharLaSesion } from '../api/cliente';
import { ingresarConEntra, modoDeIdentidad, prepararIdentidad, renovarSesionEnEntra } from './identidad';
import type { ModoDeIdentidad } from './identidad';
import {
  cerrarSesion as cerrarSesionLocal, recordarUsuarioDeLaSesion, sesionDeLaEntradaSimulada, usuarioActual, usuarioDesdeLaSesion,
} from './sesion';
import type { UsuarioSimulado } from './sesion';

export type FaseDeLaSesion =
  | { fase: 'preparando' }
  | { fase: 'entrada' }
  | { fase: 'cargando' }
  | { fase: 'error' }
  | { fase: 'sin_acceso'; sesion: SesionDelServidor | null }
  | { fase: 'lista'; sesion: SesionDelServidor; usuario: UsuarioSimulado };

export type ControlDeLaSesion = {
  modo: ModoDeIdentidad;
  estado: FaseDeLaSesion;
  /** 401 en alguna petición con la sesión abierta (HU-32a-C5). */
  caducada: boolean;
  /** «Ingresar con mi cuenta» (Entra). */
  ingresar: () => void;
  /** La entrada simulada eligió rol: se pide la sesión del actor simulado. */
  entrarSimulado: (usuario: UsuarioSimulado) => void;
  reintentar: () => void;
  /** «Volver a ingresar» del aviso de sesión caducada. */
  volverAIngresar: () => void;
  cerrarSesion: () => void;
};

export function useSesion(): ControlDeLaSesion {
  const modo = modoDeIdentidad();
  const [estado, setEstado] = useState<FaseDeLaSesion>(() => {
    if (modo === 'entra') return { fase: 'preparando' };
    return usuarioActual() ? { fase: 'cargando' } : { fase: 'entrada' };
  });
  const [caducada, setCaducada] = useState(false);

  // `vivo` + un turno por carga: una respuesta que llega con la pantalla desmontada, o después
  // de otra carga más nueva, no escribe estado.
  const vivo = useRef(true);
  const turno = useRef(0);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const aplicar = useCallback((sesion: SesionDelServidor) => {
    if (!sesion.tiene_acceso) {
      setEstado({ fase: 'sin_acceso', sesion });
      return;
    }
    const simulado = modo === 'simulado' ? usuarioActual() : null;
    const usuario = usuarioDesdeLaSesion(sesion, simulado);
    if (!usuario) {
      setEstado({ fase: 'sin_acceso', sesion });
      return;
    }
    recordarUsuarioDeLaSesion(usuario);
    setEstado({ fase: 'lista', sesion: simulado ? sesionDeLaEntradaSimulada(sesion, usuario) : sesion, usuario });
  }, [modo]);

  const cargar = useCallback(async (recienIniciada: boolean) => {
    const mio = ++turno.current;
    setEstado({ fase: 'cargando' });
    setCaducada(false);
    try {
      const sesion = recienIniciada ? await iniciarSesion() : await consultarSesion();
      if (vivo.current && mio === turno.current) aplicar(sesion);
    } catch {
      // HU-32a-C2: un token de otro tenant es 401 y se ve igual que «no responde»: la frase de
      // la especificación, sin el texto técnico del 401 ni el de MSAL.
      if (vivo.current && mio === turno.current) setEstado({ fase: 'error' });
    }
  }, [aplicar]);

  const preparar = useCallback(async () => {
    if (modo !== 'entra') {
      if (usuarioActual()) await cargar(false);
      else setEstado({ fase: 'entrada' });
      return;
    }
    setEstado({ fase: 'preparando' });
    try {
      const { hayCuenta, recienIniciada } = await prepararIdentidad();
      if (!vivo.current) return;
      if (hayCuenta) await cargar(recienIniciada);
      else setEstado({ fase: 'entrada' });
    } catch {
      if (vivo.current) setEstado({ fase: 'error' });
    }
  }, [modo, cargar]);

  useEffect(() => { preparar(); }, [preparar]);

  // El 403 vuelve a preguntar la sesión, de a uno: varias pantallas pueden recibirlo a la vez.
  const revisando = useRef(false);
  const revisarElAcceso = useCallback(async () => {
    if (revisando.current) return;
    revisando.current = true;
    try {
      const sesion = await consultarSesion();
      if (vivo.current && !sesion.tiene_acceso) setEstado({ fase: 'sin_acceso', sesion });
    } catch {
      /* si la sesión tampoco responde, cada pantalla ya muestra su propio error */
    } finally {
      revisando.current = false;
    }
  }, []);

  const fase = estado.fase;
  useEffect(() => escucharLaSesion((evento) => {
    if (fase !== 'lista') return;
    if (evento === 'caducada') setCaducada(true);
    else revisarElAcceso();
  }), [fase, revisarElAcceso]);

  const cerrarSesion = useCallback(() => {
    setCaducada(false);
    cerrarSesionLocal().catch(() => { /* lo local ya se borró; la redirección de Entra la reintenta el usuario */ });
    if (modo === 'simulado') setEstado({ fase: 'entrada' });
  }, [modo]);

  return {
    modo,
    estado,
    caducada,
    ingresar: () => { ingresarConEntra().catch(() => setEstado({ fase: 'error' })); },
    entrarSimulado: () => { cargar(false); },
    reintentar: () => { preparar(); },
    volverAIngresar: () => {
      if (modo === 'entra') renovarSesionEnEntra().catch(() => setEstado({ fase: 'error' }));
      else cerrarSesion();
    },
    cerrarSesion,
  };
}
